import { notification } from 'antd'
import { OfflineState } from '.'
import { MdCloudSync } from 'react-icons/md'
import { AiOutlineCloudSync } from 'react-icons/ai'
import offlineDB from '@/indexeddb'
import { ApplicationsState } from '../applications'
import { EApplicationCreateMode, TApplication, TSignIn } from '@/interfaces'
import { AuthState } from '../auth'
import { BsDatabaseFillCheck, BsDatabaseFillExclamation } from 'react-icons/bs'
import {
  applicationsStateActions,
  authStateActions,
  formStateActions,
  offlineStateActions,
} from '../actions'
import { decryptData } from '@/helpers'

const APPS_SYNC_LENGTH = 3

async function syncDeletedApps() {
  const deletedApps = await offlineDB.getAllFromStore(
    offlineDB.stores.DELETED_APPLIES_STORE
  )

  if (deletedApps.length === 0) return

  notification.info({
    description:
      'La sincronización de solicitudes eliminadas en el modo offline se ha iniciado',
    message: 'Sincronización de eliminaciones iniciada',
    icon: <MdCloudSync />,
    placement: 'top',
  })

  await applicationsStateActions.syncDeletedApplications(deletedApps)

  notification.info({
    message: 'Solicitudes eliminadas sincronizadas',
    icon: <BsDatabaseFillCheck />,
    placement: 'top',
  })
}

async function syncApps() {
  const apps = await offlineDB.getAllFromStore(
    offlineDB.stores.APPLICATIONS_STORE
  )
  const appsToSync = apps.filter((item) => item.toSync)

  if (appsToSync.length > 0) {
    formStateActions.closeSubsectionForm()
    await manageAppsToSync(appsToSync)
  }
}

async function manageAppsToSync(applicationsToSync: TApplication[]) {
  let batches = []
  let appsToSync = applicationsToSync

  while (appsToSync.length > 0) {
    const batch = appsToSync.slice(0, APPS_SYNC_LENGTH)
    appsToSync = appsToSync.slice(APPS_SYNC_LENGTH)

    if (batch.length > 0) {
      batches.push(batch)
    }
  }

  notification.info({
    description:
      'La sincronización de información diligenciada en el modo offline se ha iniciado',
    message: 'Sincronización de solicitudes iniciada',
    icon: <MdCloudSync />,
    placement: 'top',
  })

  for await (const batch of batches) {
    if (!OfflineState.isOnline.value) {
      notification.error({
        description:
          'Se perdió la conexión a la red mientras se ejecutaba el proceso de sincronización',
        icon: <AiOutlineCloudSync />,
        message: 'Error sincronizando solicitudes',
        placement: 'top',
      })

      return
    }

    const batchAppsIds = batch.map((item) => item.id as string)

    offlineStateActions.setCurrentSyncIds(batchAppsIds)

    await applicationsStateActions.syncApplications(batch)

    offlineStateActions.setCurrentSyncIds([])
  }

  notification.info({
    message: 'Solicitudes sincronizadas correctamente',
    icon: <BsDatabaseFillCheck />,
    placement: 'top',
  })
}

export function initSynchronizing() {
  const isCurrentlyConnected = OfflineState.isOnline.value
  const hasNetworkConnection = OfflineState.existNetworkConnection.value
  const isLoggedIn = AuthState.isLoggedIn.peek()

  if (isCurrentlyConnected && hasNetworkConnection && isLoggedIn) {
    OfflineState.isSynchronizing.value = true

    Promise.resolve()
      .then(async () => {
        await refreshUserToken()

        await initSyncAfterLogin()

        await applicationsStateActions.getAllApplications()
      })
      .finally(() => {
        OfflineState.isSynchronizing.value = false
      })
  }
}

export async function initSyncAfterLogin() {
  OfflineState.isSynchronizing.value = true

  await Promise.all([
    syncDeletedApps().catch(() => {
      notification.error({
        message: 'Error sincronizando solicitudes eliminadas',
        icon: <AiOutlineCloudSync />,
        placement: 'top',
      })
    }),
    syncApps().catch(() => {
      notification.error({
        message: 'Error sincronizando solicitudes',
        icon: <AiOutlineCloudSync />,
        placement: 'top',
      })
    }),
  ])

  OfflineState.isSynchronizing.value = false
}

export function saveCurrentApplicationsData() {
  const isOffline = !OfflineState.isOnline.value

  if (isOffline && AuthState.isLoggedIn.value) {
    const currentApplicationsData = ApplicationsState.applications
      .peek()
      .map((item) => {
        if (!item.createMode) {
          item.createMode = EApplicationCreateMode.ONLINE
        }
        return item
      })

    offlineDB
      .saveMultipleToStore(
        offlineDB.stores.APPLICATIONS_STORE,
        currentApplicationsData
      )
      .catch(() => {
        notification.error({
          message: 'Error guardando la data localmente',
          icon: <BsDatabaseFillExclamation />,
          placement: 'top',
        })
      })
      .then(() => {
        notification.success({
          message: 'Data guardada localmente',
          icon: <BsDatabaseFillCheck />,
          placement: 'top',
        })
      })
  }
}

export function updateOfflineApplicationsData() {
  const isOffline = !OfflineState.isOnline.value

  if (isOffline) {
    offlineDB.saveMultipleToStore(
      offlineDB.stores.APPLICATIONS_STORE,
      ApplicationsState.applications.value
    )
  }
}

export function updateOfflineCurrentApplicationData() {
  const currentApplication = ApplicationsState.currentApplication.value

  if (currentApplication) {
    ApplicationsState.applications.value = [
      ...ApplicationsState.applications.peek().map((item) => {
        if (item.id === currentApplication.id) {
          return { ...currentApplication }
        }

        return item
      }),
    ]

    offlineDB.saveMultipleToStore(offlineDB.stores.APPLICATIONS_STORE, [
      currentApplication,
    ])

    offlineStateActions.setOfflineStoreApplications([
      currentApplication.id as string,
    ])
  }
}

export function transFormAppDataToSyncData(
  applicationData: TApplication
): TApplication {
  const transformedData: TApplication = {}
  const CSolicitud: TApplication = {}

  const teamsIds = applicationData.teamsIds
  CSolicitud.teamsIds = teamsIds

  for (const key in applicationData) {
    const value = applicationData[key]

    if (
      key !== 'teamsIds' &&
      ((typeof value === 'object' && value !== null) || Array.isArray(value))
    ) {
      transformedData[key] = value
    } else {
      CSolicitud[key] = value
    }
  }

  delete CSolicitud.toSync
  delete CSolicitud.createMode

  return {
    CSolicitud,
    ...transformedData,
  }
}

export function cleanDBOnLogOut() {
  offlineDB.clearAllDB().then(() => {
    OfflineState.storeApplications.value = []
    console.info(`BD local reiniciada - LogOut`)
  })
  offlineDB.deleteFromStore({
    key: offlineDB.userStoreIds.application,
    storeName: offlineDB.stores.USER_STORE,
  })
}

export async function refreshUserToken(): Promise<void> {
  const isLoggedIn = AuthState.isLoggedIn.peek()

  if (isLoggedIn) {
    const refreshData = await offlineDB.getFromStore(
      offlineDB.stores.USER_STORE,
      offlineDB.userStoreIds.refreshData
    )

    const signinData: TSignIn = JSON.parse(
      decryptData(refreshData.data as string)
    ) as TSignIn

    await authStateActions.handleRefreshToken(refreshData.data as string)
  }
}
